import React, {useEffect, useMemo, useState} from 'react';
import {ActivityIndicator, TouchableOpacity, View, Text} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import Ionicons from 'react-native-vector-icons/Ionicons';
import SlideModal from '@/Atoms/SlideModal';
import useListLichessStudiesMeta, {LichessStudyMeta} from '@/Hooks/StudyGamesListApi/useListLichessStudiesMeta';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';

// Helper for Custom Checkbox matching the design
function CustomCheckbox({checked}: {checked: boolean}) {
  return (
    <View
      style={{
        width: 16,
        height: 16,
        borderRadius: 4,
        borderWidth: 1.5,
        borderColor: checked ? '#B58E58' : '#9E9E9E',
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      {checked && (
        <MaterialCommunityIcons
          name="check"
          size={11}
          color="#B58E58"
          style={{fontWeight: 'bold' as any}}
        />
      )}
    </View>
  );
}

export default function ImportLichessStudies({
  open,
  onClose,
  onImport,
  isImporting,
}: {
  open: boolean;
  onClose: () => void;
  onImport: (studyIds: string[]) => void;
  isImporting?: boolean;
}) {
  const {isMobileOrTab} = useMediaQuery();
  const [studies, setStudies] = useState<LichessStudyMeta[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loadError, setLoadError] = useState('');
  const [currentPage, setCurrentPage] = useState(0);

  const {mutate: fetchStudies, isLoading} = useListLichessStudiesMeta(
    response => {
      const list = response?.studies ?? [];
      setStudies(list);
      setSelectedIds(new Set(list.filter(s => !s.already_imported || s.needs_update).map(s => s.id)));
    },
    (error: any) => {
      setLoadError(error?.response?.data?.error || 'Could not load your Lichess studies. Please try again.');
    },
  );

  useEffect(() => {
    if (open) {
      setStudies([]);
      setSelectedIds(new Set());
      setLoadError('');
      setCurrentPage(0);
      fetchStudies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleStudy = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = studies.length > 0 && selectedIds.size === studies.length;
  const selectedCount = selectedIds.size;

  const sortedStudies = useMemo(
    () =>
      [...studies].sort((a, b) => {
        const rank = (s: LichessStudyMeta) => (!s.already_imported ? 0 : s.needs_update ? 1 : 2);
        return rank(a) - rank(b) || a.name.localeCompare(b.name);
      }),
    [studies],
  );

  const totalPages = Math.ceil(sortedStudies.length / 8);

  const paginatedStudies = useMemo(() => {
    const start = currentPage * 8;
    return sortedStudies.slice(start, start + 8);
  }, [sortedStudies, currentPage]);

  const renderBadge = (study: LichessStudyMeta) => {
    if (!study.already_imported) {
      return (
        <View style={{backgroundColor: '#E2F5F1', borderRadius: 100, paddingHorizontal: 8, paddingVertical: 2}}>
          <Text style={{fontSize: 9, fontWeight: '600', color: '#008272'}}>
            New
          </Text>
        </View>
      );
    }
    if (study.needs_update) {
      return (
        <View style={{backgroundColor: '#FFF4E3', borderRadius: 100, paddingHorizontal: 8, paddingVertical: 2}}>
          <Text style={{fontSize: 9, fontWeight: '600', color: '#A5792F'}}>
            Updated
          </Text>
        </View>
      );
    }
    return (
      <View style={{backgroundColor: '#F2F2F2', borderRadius: 100, paddingHorizontal: 8, paddingVertical: 2}}>
        <Text style={{fontSize: 9, fontWeight: '600', color: '#7A7A7A'}}>
          Imported
        </Text>
      </View>
    );
  };

  return (
    <SlideModal
      visible={open}
      onClose={onClose}
      type="center"
      showHeader={false}
      contentPadding={0}
      customStyle={{
        borderRadius: 20,
        overflow: 'hidden',
        padding: 0,
        backgroundColor: '#FFFFFF',
        width: 660,
        maxWidth: '92%',
        alignSelf: 'center',
        borderWidth: 0, // no border for the outer box
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 10},
        shadowOpacity: 0.08,
        shadowRadius: 20,
        elevation: 5,
      }}>
      
      {/* Header Block */}
      <View
        style={{
          backgroundColor: '#2C1A11',
          paddingHorizontal: 24,
          paddingVertical: 18,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
        }}>
        <View style={{flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, marginRight: 24}}>
          {/* Opaque Knight Badge */}
          <View
            style={{
              width: 38,
              height: 38,
              borderRadius: 8,
              backgroundColor: '#402C1F',
              borderWidth: 1,
              borderColor: '#B58E58',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <MaterialCommunityIcons name="chess-knight" size={18} color="#FFFFFF" />
          </View>
          <View style={{flex: 1}}>
            <Text style={{fontSize: 15, fontWeight: 'bold', color: '#FFFFFF', letterSpacing: -0.2}}>
              Import from Lichess
            </Text>
            <Text style={{fontSize: 11, color: '#A39081', marginTop: 1}}>
              Pick the studies you want to bring into your workbooks.
            </Text>
          </View>
        </View>

        {/* Close Button */}
        <TouchableOpacity
          onPress={onClose}
          activeOpacity={0.8}
          style={{
            width: 28,
            height: 28,
            borderRadius: 14,
            backgroundColor: 'rgba(255,255,255,0.08)',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Ionicons name="close-outline" size={16} color="#FFFFFF" />
        </TouchableOpacity>
      </View>

      {/* Body Content */}
      <View style={{backgroundColor: '#FFFFFF', paddingHorizontal: 24, paddingBottom: 20}}>
        {isLoading ? (
          <View style={{alignItems: 'center', justifyContent: 'center', paddingVertical: 60, gap: 12}}>
            <ActivityIndicator size="large" color="#B58E58" />
            <Text style={{fontSize: 11, color: '#7A6652'}}>Fetching your studies from Lichess…</Text>
          </View>
        ) : loadError ? (
          <View style={{alignItems: 'center', paddingVertical: 40, gap: 12}}>
            <MaterialCommunityIcons name="alert-circle-outline" size={30} color="#FF4D4D" />
            <Text style={{fontSize: 12, color: '#1E110A', textAlign: 'center'}}>{loadError}</Text>
            <TouchableOpacity
              onPress={() => { setLoadError(''); fetchStudies(); }}
              style={{
                backgroundColor: '#B58E58',
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: 100,
              }}>
              <Text style={{fontSize: 11, fontWeight: 'bold', color: '#FFFFFF'}}>Retry</Text>
            </TouchableOpacity>
          </View>
        ) : studies.length === 0 ? (
          <View style={{alignItems: 'center', paddingVertical: 40, gap: 8}}>
            <Text style={{fontSize: 12, color: '#1E110A'}}>No studies found on your Lichess account.</Text>
          </View>
        ) : (
          <>
            {/* Select All and Counter Row */}
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: 18,
                marginBottom: 12,
              }}>
              <TouchableOpacity
                activeOpacity={0.8}
                onPress={() => setSelectedIds(allSelected ? new Set() : new Set(studies.map(s => s.id)))}
                style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
                <CustomCheckbox checked={allSelected} />
                <Text style={{fontSize: 12, fontWeight: 'bold', color: '#2E1B10'}}>
                  Select all
                </Text>
              </TouchableOpacity>
              
              <Text style={{fontSize: 11, color: '#9E9E9E'}}>
                {selectedCount} of {studies.length} selected
              </Text>
            </View>

            {/* Grid Container (Non-scrollable) */}
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                rowGap: 10,
                columnGap: 10,
              }}>
              {paginatedStudies.map(study => {
                const checked = selectedIds.has(study.id);
                return (
                  <TouchableOpacity
                    key={study.id}
                    activeOpacity={0.85}
                    onPress={() => toggleStudy(study.id)}
                    style={{
                      width: isMobileOrTab ? '100%' : '49.1%',
                      flexDirection: 'row',
                      alignItems: 'center',
                      borderRadius: 10,
                      borderWidth: 1.5,
                      padding: 10,
                      borderColor: checked ? '#B58E58' : '#ECE9E6',
                      backgroundColor: checked ? '#FFFDF8' : '#FFFFFF',
                    }}>
                    
                    {/* Checkbox */}
                    <CustomCheckbox checked={checked} />

                    <View style={{flex: 1, marginLeft: 10, gap: 4}}>
                      <Text style={{fontSize: 12, fontWeight: 'bold', color: '#1E110A'}} numberOfLines={1}>
                        {study.name}
                      </Text>
                      
                      <View style={{flexDirection: 'row', alignItems: 'center', gap: 5, flexWrap: 'wrap'}}>
                        {renderBadge(study)}
                        {study.visibility === 'private' && (
                          <View style={{flexDirection: 'row', alignItems: 'center', gap: 2, marginLeft: 2}}>
                            <MaterialCommunityIcons name="lock-outline" size={10} color="#7A7A7A" />
                            <Text style={{fontSize: 9, color: '#7A7A7A'}}>Private</Text>
                          </View>
                        )}
                      </View>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>

            {/* Pagination Controls Centered Below Grid */}
            {totalPages > 1 && (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 12,
                  marginTop: 14,
                }}>
                <TouchableOpacity
                  disabled={currentPage === 0}
                  onPress={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                  style={{
                    padding: 4,
                    opacity: currentPage === 0 ? 0.35 : 1,
                  }}>
                  <MaterialCommunityIcons name="chevron-left" size={20} color="#B58E58" />
                </TouchableOpacity>
                
                <Text style={{fontSize: 11, fontWeight: 'bold', color: '#7A7A7A'}}>
                  {currentPage + 1} of {totalPages}
                </Text>

                <TouchableOpacity
                  disabled={currentPage >= totalPages - 1}
                  onPress={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                  style={{
                    padding: 4,
                    opacity: currentPage >= totalPages - 1 ? 0.35 : 1,
                  }}>
                  <MaterialCommunityIcons name="chevron-right" size={20} color="#B58E58" />
                </TouchableOpacity>
              </View>
            )}

            {/* Warning Alert Banner */}
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 8,
                backgroundColor: '#FFF9ED',
                borderRadius: 8,
                padding: 10,
                marginTop: 14,
              }}>
              <MaterialCommunityIcons name="information-outline" size={14} color="#B58E58" style={{marginTop: 1}} />
              <Text style={{fontSize: 10, color: '#7A6652', flex: 1, lineHeight: 14}}>
                Lichess limits how fast studies can be downloaded — importing fewer at a time keeps the sync quick and reliable.
              </Text>
            </View>

            {/* Footer Buttons */}
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'flex-end',
                gap: 10,
                marginTop: 14,
              }}>
              <TouchableOpacity
                onPress={onClose}
                activeOpacity={0.8}
                style={{
                  borderWidth: 1.5,
                  borderColor: '#CCCCCC',
                  backgroundColor: '#FFFFFF',
                  borderRadius: 100,
                  paddingHorizontal: 20,
                  paddingVertical: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <Text style={{fontSize: 12, fontWeight: 'bold', color: '#2E1B10'}}>
                  Cancel
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => !isImporting && selectedCount > 0 && onImport(Array.from(selectedIds))}
                activeOpacity={0.8}
                disabled={selectedCount === 0 || isLoading || !!isImporting}
                style={{
                  backgroundColor: '#EDF0F2',
                  borderRadius: 100,
                  paddingHorizontal: 22,
                  paddingVertical: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: (selectedCount === 0 || isLoading || !!isImporting) ? 0.5 : 1,
                }}>
                <Text style={{fontSize: 12, fontWeight: 'bold', color: '#2E1B10'}}>
                  {isImporting ? 'Starting import…' : `Import ${selectedCount} stud${selectedCount === 1 ? 'y' : 'ies'}`}
                </Text>
              </TouchableOpacity>
            </View>
          </>
        )}
      </View>
    </SlideModal>
  );
}
